import React, { useState } from "react";
import { useSelector } from "react-redux";

import { ParticipationDetails } from "src/app/Task/tabs/ParticipationDetails/ParticipationDetails";
import UrlDetection from "src/app/Task/tabs/PublicationSummaryTab/section/UrlDetection";
import TextDetection from "src/app/Task/tabs/PublicationSummaryTab/section/TextDetection";
import { Caption } from "src/app/Task/tabs/PublicationSummaryTab/section/Caption";
import { Published } from "src/app/Task/tabs/PublicationSummaryTab/section/Published";
import { CommentsAnalysisTab } from "src/app/Task/tabs/CommentsAnalysisTab/CommentsAnalysisTab";
import { PublicationSummaryTab } from "src/app/Task/tabs/PublicationSummaryTab/PublicationSummaryTab";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Tabs from "src/app/components/Tabs/Tabs";
import Tab from "src/app/components/Tabs/Tab";
import { SectionRowWrapper } from "src/app/components/Pane/SectionRowWrapper/SectionRowWrapper";
import { tableDataType } from "src/app/components/Table/Table";

const PublicationDetailsTabs = {
  Summary: 0,
  CommentsAnalysis: 1,
};

export default function PublicationDetails({
  publication,
  metadata,
  setShowUpdatePublicationLinkModal,
  taskOwner,
  dateCreated,
}) {
  const {
    platform,
    platformDisplayName,
    statistics,
    sentimentData,
    sentimentCommentsData,
    caption,
    publicationDate,
    synchronizable,
    synchronizationDate,
    status,
    url,
    textDetection,
    urlDetection,
  } = publication;

  const [activeTab, setActiveTab] = useState(PublicationDetailsTabs.Summary);

  const identity = useSelector((state) => state.mainReducer.identity);
  const taskDetailsLoading = useSelector(
    (state) => state.taskReducer.taskDetailsLoading,
  );

  const shouldShowSentimentData =
    identity?.sentimentsAvailability &&
    !sentimentData?.every((item) => item.value === null);

  const renderTabContent = () => {
    switch (activeTab) {
      case PublicationDetailsTabs.Summary:
        return (
          <PublicationSummaryTab
            platform={platform}
            platformDisplayName={platformDisplayName}
            statistics={statistics}
            synchronizable={synchronizable}
            synchronizationDate={synchronizationDate}
            status={status}
            setShowUpdatePublicationLinkModal={
              setShowUpdatePublicationLinkModal
            }
            publicationUrl={url}
            taskDetailsLoading={taskDetailsLoading}
          />
        );

      case PublicationDetailsTabs.CommentsAnalysis:
        if (shouldShowSentimentData) {
          return (
            <CommentsAnalysisTab
              platform={platform}
              platformDisplayName={platformDisplayName}
              sentimentData={sentimentData}
              sentimentCommentsData={sentimentCommentsData}
            />
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <>
      <ParticipationDetails
        metadata={metadata}
        taskOwner={taskOwner}
        dateCreated={dateCreated}
        dataType={tableDataType.Task}
      />

      <SectionRowWrapper className="publication__inline-sections">
        <Published publicationDate={publicationDate} />
        {caption && <Caption caption={caption} />}
        {textDetection && <TextDetection text={textDetection} />}
        {urlDetection && <UrlDetection urls={urlDetection} />}
      </SectionRowWrapper>

      <Tabs>
        <Tab
          tabText={
            <IDHFormattedMessage
              id="ws_publication_summary"
              defaultMessage="Publication summary"
            />
          }
          active={activeTab === PublicationDetailsTabs.Summary}
          onClick={() => setActiveTab(PublicationDetailsTabs.Summary)}
          medium
        />

        {shouldShowSentimentData && (
          <Tab
            tabText={
              <IDHFormattedMessage
                id="ws_comments_analysis"
                defaultMessage="Comments analysis"
              />
            }
            active={activeTab === PublicationDetailsTabs.CommentsAnalysis}
            onClick={() =>
              setActiveTab(PublicationDetailsTabs.CommentsAnalysis)
            }
            medium
          />
        )}
      </Tabs>
      {renderTabContent()}
    </>
  );
}
